import { useState, useEffect } from "react";
import { Box, TextField, Button, Alert, CircularProgress } from "@mui/material";
import { useAuthStore } from "../../store/authSlice";
import { sendMagicLink } from "../../api/auth";
import { trackAuthStart } from "../../lib/analytics";

const RESEND_COOLDOWN_SECONDS = 60;

export default function MagicLinkForm() {
  const { emailInput, magicLinkSent, magicLinkEmail, setEmailInput, setMagicLinkSent } =
    useAuthStore();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!emailInput.trim() || cooldown > 0) return;

    setError(null);
    setLoading(true);

    try {
      trackAuthStart("email_link");
      await sendMagicLink(emailInput.trim());
      setMagicLinkSent(emailInput.trim());
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (err: unknown) {
      const msg = (err as { message?: string })?.message ?? "";
      if (msg.includes("operation-not-allowed")) {
        setError("Email link sign-in is not enabled. Please contact support.");
      } else {
        setError(msg || "Failed to send magic link. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (cooldown > 0) return;
    setError(null);
    setLoading(true);
    try {
      trackAuthStart("email_link");
      await sendMagicLink(magicLinkEmail);
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch {
      setError("Failed to resend. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (magicLinkSent) {
    return (
      <Box>
        <Alert severity="success" sx={{ mb: 2 }}>
          Check your email — we sent a link to <strong>{magicLinkEmail}</strong>.<br />
          The link expires in 1 hour.
        </Alert>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Button
          onClick={handleResend}
          disabled={cooldown > 0 || loading}
          variant="text"
          size="small"
          sx={{ minHeight: 44 }}
        >
          {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend link"}
          {loading && <CircularProgress size={14} sx={{ ml: 1 }} />}
        </Button>
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit}>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <TextField
        label="Email address"
        type="email"
        fullWidth
        value={emailInput}
        onChange={(e) => setEmailInput(e.target.value)}
        required
        autoFocus
        sx={{ mb: 2 }}
        inputProps={{ "aria-label": "Email address" }}
      />
      <Button
        type="submit"
        variant="contained"
        fullWidth
        disabled={!emailInput.trim() || loading}
        sx={{ minHeight: 44 }}
      >
        {loading ? <CircularProgress size={20} color="inherit" /> : "Send magic link"}
      </Button>
    </Box>
  );
}
